import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function continueWithGoogle() {
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function GoogleG() {
    return (
        <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
    );
}

export default function AuthModal({ open, onClose }) {
    const [mode, setMode] = useState("signin");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { setUser } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            const url = mode === "signin" ? "/auth/login" : "/auth/register";
            const body = mode === "signin" ? { email, password } : { email, password, name };
            const { data } = await axios.post(`${API}${url}`, body, { withCredentials: true });
            setUser(data);
            toast.success(mode === "signin" ? "Welcome back." : "Your EDIT ID is ready.");
            onClose();
            navigate("/account");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    const forgot = async () => {
        if (!email) {
            toast.info("Enter your email above first, then tap forgot password.");
            return;
        }
        try {
            const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
            toast.info(data.message);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[70] flex items-center justify-center px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="absolute inset-0 bg-slate-950/30 backdrop-blur-sm" onClick={onClose} data-testid="auth-backdrop" />
                    <motion.div
                        initial={{ y: 32, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 24, opacity: 0 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-[32px] border border-slate-100 bg-white/90 p-8 backdrop-blur-2xl sm:p-10"
                        data-testid="auth-modal"
                    >
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            data-testid="auth-close-button"
                            className="absolute right-6 top-6 text-slate-400 transition-colors duration-300 hover:text-slate-950"
                        >
                            <X size={18} strokeWidth={1.5} />
                        </button>
                        <img src="/edit-logo.png" alt="edit." className="h-5 w-auto" />
                        <div className="mt-7 grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1" data-testid="auth-tabs">
                            {[
                                ["signin", "Sign in"],
                                ["register", "Create account"],
                            ].map(([m, label]) => (
                                <button
                                    key={m}
                                    onClick={() => setMode(m)}
                                    data-testid={`auth-tab-${m}`}
                                    className={`rounded-full py-2.5 text-sm font-light transition-colors duration-300 ${
                                        mode === m ? "bg-white text-slate-950 shadow-sm" : "text-slate-500"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <form onSubmit={submit} className="mt-8 flex flex-col gap-6" data-testid="auth-form">
                            {mode === "register" && (
                                <input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Your name"
                                    data-testid="auth-name-input"
                                    aria-label="Your name"
                                    className="w-full border-b border-slate-300 bg-transparent py-3 text-base font-extralight outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
                                />
                            )}
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email address"
                                data-testid="auth-email-input"
                                aria-label="Email address"
                                className="w-full border-b border-slate-300 bg-transparent py-3 text-base font-extralight outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
                            />
                            <input
                                type="password"
                                required
                                minLength={mode === "register" ? 8 : undefined}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
                                data-testid="auth-password-input"
                                aria-label="Password"
                                className="w-full border-b border-slate-300 bg-transparent py-3 text-base font-extralight outline-none transition-colors placeholder:text-slate-300 focus:border-slate-900"
                            />
                            {mode === "signin" && (
                                <button
                                    type="button"
                                    onClick={forgot}
                                    className="-mt-2 self-start text-xs font-light text-slate-400 transition-colors duration-300 hover:text-slate-950"
                                    data-testid="auth-forgot-button"
                                >
                                    Forgot password?
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="auth-submit-button"
                                className="mt-2 w-full rounded-full bg-slate-950 py-3.5 text-sm font-light tracking-wide text-white transition-colors duration-300 hover:bg-slate-800 active:scale-[0.98] disabled:opacity-60"
                            >
                                {loading ? "One moment…" : mode === "signin" ? "Sign in" : "Create account"}
                            </button>
                        </form>
                        <div className="my-7 flex items-center gap-4">
                            <span className="h-px flex-1 bg-slate-100" />
                            <span className="text-xs font-light text-slate-400">or</span>
                            <span className="h-px flex-1 bg-slate-100" />
                        </div>
                        <button
                            onClick={continueWithGoogle}
                            data-testid="auth-google-button"
                            className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 py-3.5 text-sm font-light text-slate-900 transition-colors duration-300 hover:bg-slate-50 active:scale-[0.98]"
                        >
                            <GoogleG /> Continue with Google
                        </button>
                        <p className="mt-6 text-center text-xs font-extralight leading-relaxed text-slate-400">
                            By continuing you agree to our{" "}
                            <a href="/terms" className="underline underline-offset-2 hover:text-slate-950" data-testid="auth-terms-link">
                                Terms
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" className="underline underline-offset-2 hover:text-slate-950" data-testid="auth-privacy-link">
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
